import React, { useMemo } from "react";
import { EntityTablePage, DeleteConfirmationConfig } from "@/components/EntityTablePage";
import { getCourseTableConfig } from "@/config/tableConfigs/courseTableConfig";
import { courseService } from "@/services/courseService";
import { CourseType } from "@/types/db/courses";
import useAuth from "@/hooks/useAuth";
import { FromDB } from "@/types";
import { NoPermissionScreen } from "@/components/screens/shared/NoPermissionScreen";
import { DeleteCourseModal } from "@/components/Modals/DeleteCourseModal";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useSetPageTitle } from "@/hooks/useSetPageTitle";

export default function CoursesPage() {
  const { userRoles, displayNames } = useAuth();
  const tableConfig = getCourseTableConfig(userRoles, displayNames);
  useSetPageTitle(displayNames?.courses?.plural);
  // Check if user has courses read permission
  const hasCoursesAccess = useMemo(() => {
    return userRoles?.some((r) => r.permissions?.courses?.read === true) ?? false;
  }, [userRoles]);

  const deleteConfirmation: DeleteConfirmationConfig<FromDB<CourseType>> = useMemo(
    () => ({
      storageKey: "course_delete_skip_confirmation",
      renderModal: ({ count, onConfirm, onCancel }) => (
        <DeleteCourseModal
          count={count}
          onConfirm={async (dontShowAgain) => {
            if (dontShowAgain) {
              await AsyncStorage.setItem("course_delete_skip_confirmation", "true");
            }
            onConfirm();
          }}
          onCancel={onCancel}
        />
      ),
    }),
    [],
  );

  if (!hasCoursesAccess) {
    return <NoPermissionScreen entityName={displayNames?.courses?.plural || "Courses"} />;
  }

  return (
    <EntityTablePage<FromDB<CourseType>>
      {...tableConfig}
      fetchData={courseService.getCourses}
      updateEntities={courseService.saveRowPatches}
      deleteEntities={courseService.deleteCoursesByIds}
      deleteConfirmation={deleteConfirmation}
    />
  );
}
